'use client';

import { type VariantProps, cva } from 'class-variance-authority';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../lib/utils';

// ============================================================================
// Variants
// ============================================================================

// purple subtle text uses an inline hex — the tertiary scale's darkest
// stop (tertiary-100 = #8b4dd6) does not pass AA on a tertiary-10 bg.
// A scale shift (new tertiary-100 = #6322ad) is planned in a follow-up
// PR that touches all tertiary consumers; until then we hardcode here.
// TODO: replace with `text-tertiary-100` after the tertiary shift lands.
const TERTIARY_DARK_HEX = '#6322ad';

const BASE_CLASSES = 'inline-flex items-center justify-center rounded-md';

const SIZE_CLASSES = {
  sm: 'h-[20px] px-1.5 text-body-12 gap-1',
  md: 'h-[24px] px-2 text-body-13 gap-1.5',
} as const;

const badgeVariants = cva(BASE_CLASSES, {
  variants: {
    variant: {
      gray: 'bg-primary-10 text-primary-80',
      green: 'bg-extra-message text-highlight-dark',
      yellow: 'bg-secondary-10 text-primary-80',
      purple: 'bg-tertiary-10',
      blue: 'bg-extra-voip-light text-extra-voip-dark',
      red: 'bg-status-danger-light text-status-danger-dark',
      orange: 'bg-status-warning-light text-status-warning-dark',
      pink: 'bg-extra-pink-light text-extra-pink-dark',
      teal: 'bg-extra-teal-light text-extra-teal-dark',
    },
    appearance: {
      subtle: '',
      solid: 'text-white-100',
    },
    size: SIZE_CLASSES,
  },
  compoundVariants: [
    // Solid uses the -dark stop for each color so white text hits AA.
    // Yellow is the exception: saturated bg + near-black text.
    { variant: 'gray', appearance: 'solid', class: 'bg-primary-100' },
    { variant: 'green', appearance: 'solid', class: 'bg-highlight-dark' },
    {
      variant: 'yellow',
      appearance: 'solid',
      class: 'bg-secondary-80 text-primary-100',
    },
    { variant: 'blue', appearance: 'solid', class: 'bg-extra-voip-dark' },
    { variant: 'red', appearance: 'solid', class: 'bg-status-danger-dark' },
    { variant: 'orange', appearance: 'solid', class: 'bg-status-warning-dark' },
    { variant: 'pink', appearance: 'solid', class: 'bg-extra-pink-dark' },
    { variant: 'teal', appearance: 'solid', class: 'bg-extra-teal-dark' },
    // purple solid bg is set via inline style — see component body.
  ],
  defaultVariants: {
    variant: 'gray',
    appearance: 'subtle',
    size: 'md',
  },
});

// ============================================================================
// Types
// ============================================================================

type Variant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

type Props = {
  /**
   * Preset color variant. Ignored when `colors` is provided.
   * @default 'gray'
   */
  variant?: Variant;
  /**
   * Visual weight. `subtle` = tinted bg + colored text (default).
   * `solid` = saturated bg + white text (yellow uses near-black text).
   * @default 'subtle'
   */
  appearance?: 'subtle' | 'solid';
  /**
   * @default 'md'
   */
  size?: 'sm' | 'md';
  /**
   * Optional element rendered before the label — icon, image, or anything
   * else. Sized by the caller.
   */
  leading?: ReactNode;
  /**
   * Optional element rendered after the label. Same contract as `leading`.
   * Pass it via this prop rather than inline children so the label keeps
   * its truncating wrapper.
   */
  trailing?: ReactNode;
  /**
   * Custom hex pair for user-defined data (e.g. call-tags whose colors
   * come from the API). When provided, beats `variant` and `appearance`.
   * Caller is responsible for AA — see `getTagColors()` from
   * `@/components/ui/color-picker`.
   */
  colors?: { text: string; background: string };
  className?: string;
  children: ReactNode;
};

// ============================================================================
// Badge
// ============================================================================

export const Badge = ({
  variant = 'gray',
  appearance = 'subtle',
  size = 'md',
  leading,
  trailing,
  colors,
  className,
  children,
}: Props) => {
  let style: CSSProperties | undefined;
  if (colors) {
    style = { color: colors.text, backgroundColor: colors.background };
  } else if (variant === 'purple') {
    style =
      appearance === 'solid'
        ? { backgroundColor: TERTIARY_DARK_HEX }
        : { color: TERTIARY_DARK_HEX };
  }

  return (
    <span
      data-slot="badge"
      className={cn(
        colors
          ? cn(BASE_CLASSES, SIZE_CLASSES[size])
          : badgeVariants({ variant, appearance, size }),
        className,
      )}
      style={style}
    >
      {leading}
      <span className="min-w-0 truncate">{children}</span>
      {trailing}
    </span>
  );
};

export type { Props as BadgeProps, Variant as BadgeVariant };
